import {AssetPartSignal, HealthIndicatorsApi} from '@domain/kiln-monitoring'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {Button, MenuItem, Skeleton, Typography, Grid, Menu} from '@mui/material'
import {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {KMS_WIDGET_NAMESPACE} from '../../consts'
import {useKiln} from '../../providers'
import {KilnMetricStatus} from '../shared'

export interface KilnHeaderMetricsProps {
  loading: boolean
  onKilnMetricClick: (localNames: string[]) => void
  healthIndicators?: HealthIndicatorsApi
  onKilnSwitch?: (kilnId: string) => void
  onCrankClick?: (kilnId: string) => void
}

export const KilnHeaderMetrics = ({
  loading,
  onKilnMetricClick,
  healthIndicators,
  onKilnSwitch,
  onCrankClick
}: KilnHeaderMetricsProps) => {
  const {t} = useTranslation(KMS_WIDGET_NAMESPACE)
  const {kilnId, kilnInfo, plantKilns} = useKiln()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const axialBalanceSignals = Array.isArray(kilnInfo?.layout?.axialBalanceSignals)
    ? []
    : kilnInfo?.layout?.axialBalanceSignals &&
      (Object.values(kilnInfo.layout.axialBalanceSignals)
        .filter((config) => config.signal !== null)
        .map((config) => config.signal) as AssetPartSignal[])

  const open = Boolean(anchorEl)
  const hasMultipleKilns = !!plantKilns?.length && plantKilns.length > 1

  const handleMetricClick = (signals?: AssetPartSignal[]) => {
    const localNames = signals?.map((signal) => signal.id)
    if (!localNames?.length) return
    onKilnMetricClick(localNames)
  }

  const gridSettings = {
    xs: 5,
    md: 'auto'
  } as const

  return (
    <Grid
      alignSelf="flex-start"
      color="text.primary"
      direction="row"
      pt={3}
      gap={{
        xs: 1,
        sm: 3
      }}
      container
      mb={2}
      data-test-id="kiln-data"
    >
      {hasMultipleKilns && onKilnSwitch && (
        <Button
          onClick={(event) => setAnchorEl(event.currentTarget)}
          variant="text"
          endIcon={<ExpandMoreIcon />}
          sx={{pl: 0, justifyContent: 'flex-start', display: 'flex'}}
          disabled={loading}
        >
          <Typography variant="h4" color="text.primary">
            {loading ? (
              <Skeleton variant="text" width={60} height={30} />
            ) : (
              kilnInfo?.name || 'Unnamed kiln'
            )}
          </Typography>
        </Button>
      )}

      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        {plantKilns?.map((kiln) => (
          <MenuItem
            key={kiln.id}
            selected={kiln.id === kilnId}
            onClick={() => {
              onKilnSwitch?.(kiln.id)
              setAnchorEl(null)
            }}
          >
            {kiln.name || kiln.id}
          </MenuItem>
        ))}
      </Menu>

      <Grid item xs={gridSettings.xs} sm={gridSettings.md}>
        <KilnMetricStatus
          title={t('kilnMetrics.speed')}
          value={`${healthIndicators?.speed?.value?.toFixed(1) ?? '-'} ${
            healthIndicators?.speed?.unit ?? ''
          }`}
          status={healthIndicators?.speed?.status}
          loading={loading}
          onClick={() => handleMetricClick(kilnInfo?.layout?.speedSignals)}
          signals={kilnInfo?.layout?.speedSignals}
        />
      </Grid>
      <Grid item xs={gridSettings.xs} sm={gridSettings.md}>
        <KilnMetricStatus
          title={t('kilnMetrics.feed')}
          value={`${healthIndicators?.feed?.value?.toFixed(0) ?? '-'} ${
            healthIndicators?.feed?.unit ?? ''
          }`}
          status={healthIndicators?.feed?.status}
          loading={loading}
          onClick={() => handleMetricClick(kilnInfo?.layout?.feedSignals)}
          signals={kilnInfo?.layout?.feedSignals}
        />
      </Grid>
      <Grid item xs={gridSettings.xs} sm={gridSettings.md}>
        <KilnMetricStatus
          title={t('kilnMetrics.ovality')}
          loading={loading}
          status={healthIndicators?.ovality?.status}
          onClick={() => handleMetricClick(kilnInfo?.layout?.ovalitySignals)}
          signals={kilnInfo?.layout?.ovalitySignals}
          disabled={!kilnInfo?.layout?.ovalitySignals?.length}
        />
      </Grid>
      <Grid item xs={gridSettings.xs} sm={gridSettings.md}>
        <KilnMetricStatus
          title={t('kilnMetrics.axialBalance')}
          loading={loading}
          onClick={() => handleMetricClick(axialBalanceSignals)}
          signals={axialBalanceSignals}
          disabled={!axialBalanceSignals?.length}
          status={!axialBalanceSignals?.length ? undefined : 'SignalsCount'}
          value={!axialBalanceSignals?.length ? undefined : axialBalanceSignals?.length?.toString()}
        />
      </Grid>
      <Grid item xs={gridSettings.xs} sm={gridSettings.md}>
        <KilnMetricStatus
          title={t('kilnMetrics.warp')}
          loading={loading}
          status={healthIndicators?.crank?.status}
          onClick={() => onCrankClick?.(kilnId)}
          disabled={
            !kilnInfo?.layout?.crankSignals || !Object.keys(kilnInfo.layout.crankSignals).length
          }
          signals={kilnInfo?.layout?.crankSignals}
        />
      </Grid>
    </Grid>
  )
}
